import React, { useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import {
  FundoForm, Classe, Conta, VinculoPrestador,
  classeVazia, contaVazia, prestadorVazio,
} from '../fundo_liquido/types';
import { Opt, BENCHMARK, TIPO_CONTA, PAPEL } from '../fundo_liquido/mock';
import { ClasseSubform, ContaSubform, PrestadorSubform } from '../fundo_liquido/subforms';

// Edição das coleções 1:N do fundo (classes, contas, prestadores) no detalhe.
// Reaproveita os mesmos subforms do wizard; o PUT reconcilia por id (com id=UPDATE,
// sem id=INSERT, ausente=soft-delete) — por isso os itens vindos da API mantêm o id.

let _seq = 1;
const novaKey = () => `kd${_seq++}`;
const lbl = (opts: Opt[], v?: string) => opts.find((o) => o.value === v)?.label || (v || '—');

type SubAberto =
  | { tipo: 'classe'; item: Classe }
  | { tipo: 'conta'; item: Conta }
  | { tipo: 'prestador'; item: VinculoPrestador }
  | null;

interface Props {
  form: FundoForm;
  onChange: (patch: Partial<FundoForm>) => void;
  prestadorOpts: Opt[];
}

const Secao: React.FC<{ titulo: string; addLabel: string; onAdd: () => void; children: React.ReactNode }> = ({ titulo, addLabel, onAdd, children }) => (
  <div className="fl-rev-section">
    <div className="fd-col-head">
      <div className="fd-col-title">{titulo}</div>
      <button type="button" className="fl-add-btn" onClick={onAdd}><Plus size={16} /> {addLabel}</button>
    </div>
    {children}
  </div>
);

const Acoes: React.FC<{ onEdit: () => void; onDel: () => void }> = ({ onEdit, onDel }) => (
  <td className="fl-col-acoes">
    <div className="fl-row-actions">
      <button type="button" className="fl-icon-btn" title="Editar" onClick={onEdit}><Pencil size={15} /></button>
      <button type="button" className="fl-icon-btn danger" title="Remover" onClick={onDel}><Trash2 size={15} /></button>
    </div>
  </td>
);

const ColecoesEdit: React.FC<Props> = ({ form, onChange, prestadorOpts }) => {
  const [sub, setSub] = useState<SubAberto>(null);

  const upsert = <T extends { _key: string }>(lista: T[], item: T): T[] => {
    const i = lista.findIndex((x) => x._key === item._key);
    if (i === -1) return [...lista, item];
    const novo = [...lista]; novo[i] = item; return novo;
  };
  const salvar = (item: any) => {
    if (!sub) return;
    if (sub.tipo === 'classe') onChange({ classes: upsert(form.classes, item) });
    if (sub.tipo === 'conta') onChange({ contas: upsert(form.contas, item) });
    if (sub.tipo === 'prestador') onChange({ prestadores: upsert(form.prestadores, item) });
    setSub(null);
  };
  const remover = (campo: 'classes' | 'contas' | 'prestadores', key: string) =>
    onChange({ [campo]: (form[campo] as any[]).filter((x) => x._key !== key) } as Partial<FundoForm>);

  const nomePrestador = (p: VinculoPrestador) =>
    p.prestador_nome || prestadorOpts.find((o) => String(o.value) === String(p.prestador_id))?.label || '—';

  return (
    <>
      <Secao titulo={`Classes / Séries (${form.classes.length})`} addLabel="Adicionar classe"
        onAdd={() => setSub({ tipo: 'classe', item: classeVazia(novaKey()) })}>
        {form.classes.length === 0 ? <div className="fl-empty">Nenhuma classe.</div> : (
          <table className="fl-table">
            <thead><tr><th>Subclasse</th><th>Nome</th><th>Benchmark</th><th>Tx. adm.</th><th>Status</th><th className="fl-col-acoes" /></tr></thead>
            <tbody>
              {form.classes.map((c) => (
                <tr key={c._key}>
                  <td>{c.subclasse}</td>
                  <td>{c.nome || '—'}</td>
                  <td>{lbl(BENCHMARK, c.benchmark)}</td>
                  <td>{c.condicoes?.tx_adm ? `${c.condicoes.tx_adm}%` : '—'}</td>
                  <td><span className={`fl-badge ${c.is_active ? 'fl-badge--on' : 'fl-badge--off'}`}>{c.is_active ? 'Ativa' : 'Inativa'}</span></td>
                  <Acoes onEdit={() => setSub({ tipo: 'classe', item: c })} onDel={() => remover('classes', c._key)} />
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Secao>

      <Secao titulo={`Contas (${form.contas.length})`} addLabel="Adicionar conta"
        onAdd={() => setSub({ tipo: 'conta', item: contaVazia(novaKey()) })}>
        {form.contas.length === 0 ? <div className="fl-empty">Nenhuma conta.</div> : (
          <table className="fl-table">
            <thead><tr><th>Tipo</th><th>Código</th><th>Banco</th><th>Agência</th><th>Conta</th><th>Favorecido</th><th className="fl-col-acoes" /></tr></thead>
            <tbody>
              {form.contas.map((c) => (
                <tr key={c._key}>
                  <td>{lbl(TIPO_CONTA, c.tipo)}</td><td>{c.codigo || '—'}</td><td>{c.banco || '—'}</td>
                  <td>{c.agencia || '—'}</td><td>{c.numero_conta || '—'}</td><td>{c.favorecido || '—'}</td>
                  <Acoes onEdit={() => setSub({ tipo: 'conta', item: c })} onDel={() => remover('contas', c._key)} />
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Secao>

      <Secao titulo={`Prestadores (${form.prestadores.length})`} addLabel="Adicionar prestador"
        onAdd={() => setSub({ tipo: 'prestador', item: prestadorVazio(novaKey()) })}>
        {form.prestadores.length === 0 ? <div className="fl-empty">Nenhum prestador.</div> : (
          <table className="fl-table">
            <thead><tr><th>Prestador</th><th>Papel</th><th>Início</th><th>Fim</th><th className="fl-col-acoes" /></tr></thead>
            <tbody>
              {form.prestadores.map((p) => (
                <tr key={p._key}>
                  <td>{nomePrestador(p)}</td><td>{lbl(PAPEL, p.papel)}</td><td>{p.data_inicio || '—'}</td><td>{p.data_fim || '—'}</td>
                  <Acoes onEdit={() => setSub({ tipo: 'prestador', item: p })} onDel={() => remover('prestadores', p._key)} />
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Secao>

      {sub?.tipo === 'classe' && <ClasseSubform initial={sub.item} onSave={salvar} onClose={() => setSub(null)} />}
      {sub?.tipo === 'conta' && <ContaSubform initial={sub.item} onSave={salvar} onClose={() => setSub(null)} />}
      {sub?.tipo === 'prestador' && <PrestadorSubform initial={sub.item} options={prestadorOpts} onSave={salvar} onClose={() => setSub(null)} />}
    </>
  );
};

export default ColecoesEdit;
